// src/components/provider/JobListing.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { addJobListing, updateJobListing, deleteJobListing, getProviderJobs } from '../../services/firestore';

const JobListing = () => {
  const { currentUser, userProfile } = useAuth();
  
  // State for job listings
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for new job form
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: '',
    duration: 'temporary', // 'temporary', 'part-time', 'full-time'
    startDate: '',
    endDate: '',
    schedule: {
      monday: { selected: false, startTime: '', endTime: '' },
      tuesday: { selected: false, startTime: '', endTime: '' },
      wednesday: { selected: false, startTime: '', endTime: '' },
      thursday: { selected: false, startTime: '', endTime: '' },
      friday: { selected: false, startTime: '', endTime: '' },
      saturday: { selected: false, startTime: '', endTime: '' },
      sunday: { selected: false, startTime: '', endTime: '' }
    },
    hourlyRate: '',
    qualifications: [],
    responsibilities: []
  });
  
  // State for editing
  const [editingJobId, setEditingJobId] = useState(null);
  
  // List states
  const [newQualification, setNewQualification] = useState('');
  const [newResponsibility, setNewResponsibility] = useState('');
  
  // Fetch provider's jobs
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        if (currentUser) {
          const jobsList = await getProviderJobs(currentUser.uid);
          setJobs(jobsList);
        }
      } catch (err) {
        setError('Error fetching jobs: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchJobs();
  }, [currentUser]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle schedule changes
  const handleScheduleChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          [field]: field === 'selected' ? value : value
        }
      }
    }));
  };
  
  // Add a new qualification
  const handleAddQualification = () => {
    if (newQualification.trim()) {
      setFormData(prev => ({
        ...prev,
        qualifications: [...prev.qualifications, newQualification.trim()]
      }));
      setNewQualification('');
    }
  };
  
  // Remove a qualification
  const handleRemoveQualification = (index) => {
    setFormData(prev => ({
      ...prev,
      qualifications: prev.qualifications.filter((_, i) => i !== index)
    }));
  };
  
  // Add a new responsibility
  const handleAddResponsibility = () => {
    if (newResponsibility.trim()) {
      setFormData(prev => ({
        ...prev,
        responsibilities: [...prev.responsibilities, newResponsibility.trim()]
      }));
      setNewResponsibility('');
    }
  };
  
  // Remove a responsibility
  const handleRemoveResponsibility = (index) => {
    setFormData(prev => ({
      ...prev,
      responsibilities: prev.responsibilities.filter((_, i) => i !== index)
    }));
  };
  
  // Reset form to default values
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      requirements: '',
      duration: 'temporary',
      startDate: '',
      endDate: '',
      schedule: {
        monday: { selected: false, startTime: '', endTime: '' },
        tuesday: { selected: false, startTime: '', endTime: '' },
        wednesday: { selected: false, startTime: '', endTime: '' },
        thursday: { selected: false, startTime: '', endTime: '' },
        friday: { selected: false, startTime: '', endTime: '' },
        saturday: { selected: false, startTime: '', endTime: '' },
        sunday: { selected: false, startTime: '', endTime: '' }
      },
      hourlyRate: '',
      qualifications: [],
      responsibilities: []
    });
    setEditingJobId(null);
    setNewQualification('');
    setNewResponsibility('');
  };
  
  // Open form for editing a job
  const handleEditJob = (job) => {
    // Format the schedule data correctly
    const formattedSchedule = {};
    
    Object.keys(job.schedule || {}).forEach(day => {
      formattedSchedule[day] = {
        selected: Boolean(job.schedule[day].startTime || job.schedule[day].endTime),
        startTime: job.schedule[day].startTime || '',
        endTime: job.schedule[day].endTime || ''
      };
    });
    
    setFormData({
      title: job.title || '',
      description: job.description || '',
      requirements: job.requirements || '',
      duration: job.duration || 'temporary',
      startDate: job.startDate || '',
      endDate: job.endDate || '',
      schedule: formattedSchedule,
      hourlyRate: job.hourlyRate || '',
      qualifications: job.qualifications || [],
      responsibilities: job.responsibilities || []
    });
    setEditingJobId(job.id);
    setShowForm(true);
    
    // Scroll to form
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.title || !formData.description || !formData.duration) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Filter out days where selected is false
      const filteredSchedule = {};
      Object.keys(formData.schedule).forEach(day => {
        if (formData.schedule[day].selected) {
          filteredSchedule[day] = {
            startTime: formData.schedule[day].startTime,
            endTime: formData.schedule[day].endTime
          };
        }
      });
      
      const jobData = {
        ...formData,
        schedule: filteredSchedule,
        providerName: userProfile?.displayName || 'Provider',
        providerUid: currentUser.uid,
        parentUid: null
      };
      
      // For temporary jobs, check if dates are provided
      if (formData.duration === 'temporary') {
        // If both start and end dates are provided, no need for recurring schedule
        if (formData.startDate && formData.endDate) {
          // Remove schedule for non-recurring temporary jobs
          delete jobData.schedule;
        }
      }
      
      if (editingJobId) {
        // Update existing job
        await updateJobListing(editingJobId, jobData);
        
        // Update local state
        setJobs(prev => prev.map(job => 
          job.id === editingJobId ? { ...job, ...jobData } : job
        ));
      } else {
        // Create new job
        const newJob = await addJobListing(jobData);
        
        // Update local state
        setJobs(prev => [newJob, ...prev]);
      }
      
      // Reset form
      resetForm();
      setShowForm(false);
    } catch (err) {
      setError('Error saving job: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle job deletion
  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      setLoading(true);
      
      try {
        await deleteJobListing(jobId);
        
        // Update local state
        setJobs(prev => prev.filter(job => job.id !== jobId));
      } catch (err) {
        setError('Error deleting job: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  
  // Get days of week as string
  const getScheduleString = (schedule) => {
    if (!schedule || Object.keys(schedule).length === 0) return 'No schedule set';
    
    const days = Object.keys(schedule).map(day => {
      const { startTime, endTime } = schedule[day];
      if (startTime && endTime) {
        return `${day.charAt(0).toUpperCase() + day.slice(1)}: ${startTime} - ${endTime}`;
      }
      return null;
    }).filter(Boolean);
    
    return days.join(', ');
  };
  
  return (
    <div className="job-listing">
      <h1>Manage Job Listings</h1>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="action-buttons">
        <button 
          className="primary-button"
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
          }}
        >
          {showForm ? 'Cancel' : 'Post New Job'}
        </button>
      </div>
      
      {/* Add/Edit Job Form */}
      {showForm && (
        <div className="job-form-container form-section">
          <h2>{editingJobId ? 'Edit Job' : 'Post New Job'}</h2>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Job Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Part-time Nanny, Early Childhood Educator"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Job Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe the job role and expectations"
                rows={4}
                required
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="duration">Duration *</label>
                <select
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  required
                >
                  <option value="temporary">Temporary</option>
                  <option value="part-time">Part-time</option>
                  <option value="full-time">Full-time</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="hourlyRate">Hourly Rate ($)</label>
                <input
                  type="number"
                  id="hourlyRate"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleChange}
                  placeholder="Enter hourly rate"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  min={formData.startDate}
                />
              </div>
            </div>
            
            {/* Don't show schedule if temporary job with specific dates */}
            {!(formData.duration === 'temporary' && formData.startDate && formData.endDate) && (
              <div className="form-group">
                <label>Schedule</label>
                <div className="schedule-container">
                  {Object.keys(formData.schedule).map(day => (
                    <div className="schedule-day" key={day}>
                      <div className="day-checkbox">
                        <input
                          type="checkbox"
                          id={`${day}-checkbox`}
                          checked={formData.schedule[day].selected}
                          onChange={(e) => handleScheduleChange(day, 'selected', e.target.checked)}
                        />
                        <label htmlFor={`${day}-checkbox`}>
                          {day.charAt(0).toUpperCase() + day.slice(1)}
                        </label>
                      </div>
                      
                      {formData.schedule[day].selected && (
                        <div className="day-hours">
                          <input
                            type="time"
                            value={formData.schedule[day].startTime}
                            onChange={(e) => handleScheduleChange(day, 'startTime', e.target.value)}
                            placeholder="Start"
                          />
                          <span>to</span>
                          <input
                            type="time"
                            value={formData.schedule[day].endTime}
                            onChange={(e) => handleScheduleChange(day, 'endTime', e.target.value)}
                            placeholder="End"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="form-group">
              <label>Qualifications</label>
              <div className="list-input-container">
                <div className="list-input">
                  <input
                    type="text"
                    value={newQualification}
                    onChange={(e) => setNewQualification(e.target.value)}
                    placeholder="Add a required qualification"
                  />
                  <button 
                    type="button" 
                    className="add-button"
                    onClick={handleAddQualification}
                  >
                    Add
                  </button>
                </div>
                
                <ul className="items-list">
                  {formData.qualifications.map((qualification, index) => (
                    <li key={index} className="list-item">
                      <span>{qualification}</span>
                      <button 
                        type="button" 
                        className="remove-button"
                        onClick={() => handleRemoveQualification(index)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="form-group">
              <label>Responsibilities</label>
              <div className="list-input-container">
                <div className="list-input">
                  <input
                    type="text"
                    value={newResponsibility}
                    onChange={(e) => setNewResponsibility(e.target.value)}
                    placeholder="Add a job responsibility"
                  />
                  <button 
                    type="button" 
                    className="add-button"
                    onClick={handleAddResponsibility}
                  >
                    Add
                  </button>
                </div>
                
                <ul className="items-list">
                  {formData.responsibilities.map((responsibility, index) => (
                    <li key={index} className="list-item">
                      <span>{responsibility}</span>
                      <button 
                        type="button" 
                        className="remove-button"
                        onClick={() => handleRemoveResponsibility(index)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="requirements">Additional Requirements</label>
              <textarea
                id="requirements"
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                placeholder="Any additional requirements or notes"
                rows={3}
              />
            </div>
            
            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-button"
                disabled={loading}
              >
                {loading ? 'Saving...' : (editingJobId ? 'Update Job' : 'Post Job')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Jobs List */}
      <div className="jobs-list">
        <h2>Your Job Listings</h2>
        
        {loading && <p>Loading jobs...</p>}
        
        {!loading && jobs.length === 0 && (
          <p className="no-jobs">You haven't posted any jobs yet.</p>
        )}
        
        {jobs.map(job => (
          <div className="job-card" key={job.id}>
            <div className="job-header">
              <h3>{job.title}</h3>
              <span className={`job-type ${job.duration || ''}`}>
                {job.duration ? job.duration.charAt(0).toUpperCase() + job.duration.slice(1) : 'Job'}
              </span>
            </div>
            
            <div className="job-details">
              <p className="job-description">{job.description}</p>
              
              <div className="job-info">
                {job.startDate && (
                  <p>
                    <strong>Period: </strong>
                    {formatDate(job.startDate)}
                    {job.endDate && ` to ${formatDate(job.endDate)}`}
                  </p>
                )}
                
                {job.schedule && Object.keys(job.schedule).length > 0 && (
                  <p>
                    <strong>Schedule: </strong>
                    {getScheduleString(job.schedule)}
                  </p>
                )}
                
                {job.hourlyRate && (
                  <p>
                    <strong>Rate: </strong>
                    ${job.hourlyRate}/hour
                  </p>
                )}
              </div>
              
              {(job.qualifications?.length > 0 || job.responsibilities?.length > 0) && (
                <div className="job-lists">
                  {job.qualifications?.length > 0 && (
                    <div className="job-qualifications">
                      <h4>Qualifications</h4>
                      <ul>
                        {job.qualifications.map((qualification, index) => (
                          <li key={index}>{qualification}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {job.responsibilities?.length > 0 && (
                    <div className="job-responsibilities">
                      <h4>Responsibilities</h4>
                      <ul>
                        {job.responsibilities.map((responsibility, index) => (
                          <li key={index}>{responsibility}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {job.requirements && (
                <div className="job-requirements">
                  <h4>Additional Requirements</h4>
                  <p>{job.requirements}</p>
                </div>
              )}
            </div>
            
            <div className="job-actions">
              <button 
                className="edit-button"
                onClick={() => handleEditJob(job)}
              >
                Edit
              </button>
              <button 
                className="delete-button"
                onClick={() => handleDeleteJob(job.id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default JobListing;